/**
 * International Telephone Input Custom Element
 *
 * A web component wrapper for intl-tel-input that provides:
 * - Automatic country detection based on user locale (AVT.lang or browser)
 * - i18n support with 44 languages for country names
 * - Prevention of unwanted auto-country-switching during input
 * - Proper validation and formatting for international phone numbers
 */

import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import { ar, bg, bn, bs, ca, cs, da, de, ee, el, en, es, fa, fi, fr, hi, hr, hu, id, it, ja, ko, lt, mr, nl, no, pl, pt, ro, ru, sk, sl, sq, sr, sv, te, th, tr, uk, ur, uz, vi, zh } from 'intl-tel-input/i18n';

// i18n translations for country names (44 languages)
const i18nTranslations = {
  ar, bg, bn, bs, ca, cs, da, de, ee, el, en, es,
  fa, fi, fr, hi, hr, hu, id, it, ja, ko, lt, mr,
  nl, no, pl, pt, ro, ru, sk, sl, sq, sr, sv, te,
  th, tr, uk, ur, uz, vi, zh
};

// All 249 countries supported by intl-tel-input v25.12.4
const VALID_COUNTRY_CODES = new Set([
  'af', 'ax', 'al', 'dz', 'as', 'ad', 'ao', 'ai', 'ag', 'ar', 'am', 'aw', 'ac',
  'au', 'at', 'az', 'bs', 'bh', 'bd', 'bb', 'by', 'be', 'bz', 'bj', 'bm', 'bt',
  'bo', 'ba', 'bw', 'br', 'io', 'vg', 'bn', 'bg', 'bf', 'bi', 'kh', 'cm', 'ca',
  'cv', 'bq', 'ky', 'cf', 'td', 'cl', 'cn', 'cx', 'cc', 'co', 'km', 'cg', 'cd',
  'ck', 'cr', 'ci', 'hr', 'cu', 'cw', 'cy', 'cz', 'dk', 'dj', 'dm', 'do', 'ec',
  'eg', 'sv', 'gq', 'er', 'ee', 'sz', 'et', 'fk', 'fo', 'fj', 'fi', 'fr', 'gf',
  'pf', 'ga', 'gm', 'ge', 'de', 'gh', 'gi', 'gr', 'gl', 'gd', 'gp', 'gu', 'gt',
  'gg', 'gn', 'gw', 'gy', 'ht', 'hn', 'hk', 'hu', 'is', 'in', 'id', 'ir', 'iq',
  'ie', 'im', 'il', 'it', 'jm', 'jp', 'je', 'jo', 'kz', 'ke', 'ki', 'xk', 'kw',
  'kg', 'la', 'lv', 'lb', 'ls', 'lr', 'ly', 'li', 'lt', 'lu', 'mo', 'mg', 'mw',
  'my', 'mv', 'ml', 'mt', 'mh', 'mq', 'mr', 'mu', 'yt', 'mx', 'fm', 'md', 'mc',
  'mn', 'me', 'ms', 'ma', 'mz', 'mm', 'na', 'nr', 'np', 'nl', 'nc', 'nz', 'ni',
  'ne', 'ng', 'nu', 'nf', 'kp', 'mk', 'mp', 'no', 'om', 'pk', 'pw', 'ps', 'pa',
  'pg', 'py', 'pe', 'ph', 'pl', 'pt', 'pr', 'qa', 're', 'ro', 'ru', 'rw', 'ws',
  'sm', 'st', 'sa', 'sn', 'rs', 'sc', 'sl', 'sg', 'sx', 'sk', 'si', 'sb', 'so',
  'za', 'kr', 'ss', 'es', 'lk', 'bl', 'sh', 'kn', 'lc', 'mf', 'pm', 'vc', 'sd',
  'sr', 'sj', 'se', 'ch', 'sy', 'tw', 'tj', 'tz', 'th', 'tl', 'tg', 'tk', 'to',
  'tt', 'tn', 'tr', 'tm', 'tc', 'tv', 'vi', 'ug', 'ua', 'ae', 'gb', 'us', 'uy',
  'uz', 'vu', 'va', 've', 'vn', 'wf', 'eh', 'ye', 'zm', 'zw'
]);

/**
 * Validates if a country code is supported by intl-tel-input
 * @param {string} code - The country code to validate
 * @returns {boolean} True if the code is valid and supported
 */
function isValidCountryCode(code) {
  return typeof code === 'string' &&
         code.length === 2 &&
         VALID_COUNTRY_CODES.has(code.toLowerCase());
}

/**
 * Gets the user's locale with fallback chain
 * @returns {string} The user's locale (e.g., "en_CA", "en-US")
 */
function getUserLocale() {
  // 1. Try AVT.lang (simple string like "en_CA")
  if (window.AVT?.lang && typeof window.AVT.lang === 'string') {
    return window.AVT.lang;
  }
  // 2. Fallback to browser language
  if (navigator.language) {
    return navigator.language;
  }
  // 3. IE fallback
  if (navigator.userLanguage) {
    return navigator.userLanguage;
  }
  // 4. Default
  return 'en-US';
}

/**
 * Extracts and validates country code from locale string
 * @param {string} locale - The locale string (e.g., "en_CA", "en-US", "fr")
 * @returns {string} Validated lowercase country code or "us" fallback
 */
function getCountryCodeFromLocale(locale) {
  // Only split if contains delimiter
  if (locale.includes('_') || locale.includes('-')) {
    const parts = locale.split(/[_-]/);
    if (parts.length >= 2) {
      const countryCode = parts[1].toLowerCase();
      // Validate against actual supported countries
      if (isValidCountryCode(countryCode)) {
        return countryCode;
      }
    }
  }

  // No valid country part - map language to default country
  const language = locale.split(/[_-]/)[0].toLowerCase();
  const langToCountry = {
    en: 'us', fr: 'fr', de: 'de', es: 'es', pt: 'pt', it: 'it',
    ja: 'jp', zh: 'cn', ko: 'kr', ar: 'sa', hi: 'in', bn: 'bd',
    nl: 'nl', pl: 'pl', ru: 'ru', tr: 'tr', vi: 'vn', th: 'th',
    id: 'id', sv: 'se', da: 'dk', no: 'no', fi: 'fi', cs: 'cz',
    el: 'gr', hu: 'hu', ro: 'ro', uk: 'ua', sk: 'sk', bg: 'bg',
    hr: 'hr', lt: 'lt', lv: 'lv', et: 'ee', sl: 'si', ms: 'my',
    fa: 'ir', ur: 'pk', he: 'il', ca: 'es', sr: 'rs'
  };

  const mappedCountry = langToCountry[language];
  if (mappedCountry && isValidCountryCode(mappedCountry)) {
    return mappedCountry;
  }

  // Final fallback
  return 'us';
}

/**
 * Extracts base language code from locale for i18n translations
 * @param {string} locale - The locale string (e.g., "en_CA", "fr-FR")
 * @returns {string} Base language code, defaults to 'en' if not supported
 */
function mapLocaleToLanguage(locale) {
  const language = locale.split(/[_-]/)[0].toLowerCase();
  return i18nTranslations[language] ? language : 'en';
}

/**
 * Gets i18n translations for intl-tel-input based on user locale
 * @param {string} locale - The user's locale
 * @returns {Object} Translation object
 */
function getTranslations(locale) {
  // Try user's language
  const language = mapLocaleToLanguage(locale);
  if (i18nTranslations[language]) {
    return i18nTranslations[language];
  }

  // Try browser language
  const browserLocale = navigator.language || navigator.userLanguage;
  if (browserLocale && browserLocale !== locale) {
    const browserLanguage = mapLocaleToLanguage(browserLocale);
    if (i18nTranslations[browserLanguage]) {
      return i18nTranslations[browserLanguage];
    }
  }

  // Final fallback: English
  return i18nTranslations.en;
}

class IntlTelInputElement extends HTMLElement {
  constructor() {
    super();
    this.iti = null;
    this.input = null;
    this.styleInjected = false;
    this.userPreferredCountry = null; // Track user's preferred country
    this.allowCountryChange = false; // Flag to allow intentional country changes
    this.utilsReady = false; // True after intl-tel-input utils finish loading (required for isValidNumber)
  }

  static get observedAttributes() {
    return ['disabled', 'placeholder'];
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    if (name === 'disabled' && this.input) {
      const isDisabled = newValue !== null;
      this.input.disabled = isDisabled;

      // Update the intl-tel-input disabled state
      if (this.iti) {
        // Preserve the current value and country before re-initialization
        const currentValue = this.iti.getNumber();
        const currentCountry = this.userPreferredCountry || this.iti.getSelectedCountryData()?.iso2;
        this.iti.destroy();
        this.initializeIntlTelInput();

        // Restore the preserved country
        if (currentCountry) {
          this.userPreferredCountry = currentCountry;
          this.iti.setCountry(currentCountry);
        }

        if (currentValue) {
          this.iti.setNumber(currentValue);
        }

        // Update placeholder after re-initialization
        this.updatePlaceholder();
      }
    }

    if (name === 'placeholder' && this.input) {
      this.updatePlaceholder();
    }
  }

  addLayoutStyles() {
    // Only inject layout styles once
    if (document.getElementById('intl-tel-input-layout-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'intl-tel-input-layout-styles';
    style.textContent = `
      /* Layout styles - ensure full width and proper structure */
      [data-intl-tel-input-init="true"] .iti {
        width: 100% !important;
        min-width: 0 !important;
        height: 40px !important;
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        position: relative !important;
      }

      [data-intl-tel-input-init="true"] .iti .iti__flag-container {
        width: auto !important;
        height: 100% !important;
        flex-shrink: 0 !important;
        flex-grow: 0 !important;
        display: flex !important;
        align-items: center !important;
      }

      [data-intl-tel-input-init="true"] .iti .iti__selected-flag {
        width: auto !important;
        height: 100% !important;
        flex-shrink: 0 !important;
        flex-grow: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }

      [data-intl-tel-input-init="true"] .iti .iti__selected-dial-code {
        display: inline-flex !important;
        align-items: center !important;
        height: 100% !important;
        line-height: 1 !important;
        vertical-align: middle !important;
      }

      [data-intl-tel-input-init="true"] .iti input.iti__tel-input {
        width: 100% !important;
        height: 40px !important;
        min-width: 0 !important;
        max-width: 100% !important;
        flex: 1 1 0% !important;
        box-sizing: border-box !important;
        flex-grow: 1 !important;
        flex-shrink: 1 !important;
        flex-basis: 0% !important;
      }
    `;
    document.head.appendChild(style);
  }

  addDisabledStyles() {
    // Only inject styles once
    if (this.styleInjected || document.getElementById('intl-tel-input-disabled-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'intl-tel-input-disabled-styles';
    style.textContent = `
      /* Base styles for all intl-tel-input elements */

      /* Ensure the intl-tel-input container has proper structure */
      intl-tel-input .iti {
        position: relative !important;
      }

      /* Placeholder always gray #9DACC4 in both enabled and disabled states */
      intl-tel-input input[type="tel"]::placeholder {
        color: #9DACC4 !important;
        opacity: 1 !important;
      }

      /* Enabled state - black text */
      intl-tel-input:not([disabled]) input[type="tel"]:not(:disabled) {
        color: #000000 !important;
      }

      /* Country code always gray in all states */
      intl-tel-input .iti__selected-dial-code {
        color: #9DACC4 !important;
      }

      /* Disabled state styles */
      intl-tel-input[disabled] input[type="tel"],
      intl-tel-input input[type="tel"]:disabled {
        color: #9DACC4 !important;
        background-color: #f9fafb !important;
        cursor: not-allowed !important;
        opacity: 1 !important;
      }

      /* Flag in disabled state */
      intl-tel-input[disabled] .iti__selected-flag {
        opacity: 1 !important;
        pointer-events: none !important;
      }


      /* Add vertical border between flag dropdown and input area */
      intl-tel-input .iti__flag-container {
        border-right: 1px solid #d1d5db !important;
        padding-right: 10px !important;
        margin-right: 10px !important;
      }

      /* Ensure the selected flag area has proper padding */
      intl-tel-input .iti__selected-flag {
        padding-right: 12px !important;
        padding-left: 8px !important;
      }

      /* Add vertical separator before dial code with more spacing */
      intl-tel-input .iti__selected-dial-code {
        border-left: 1px solid #d1d5db !important;
        padding-left: 10px !important;
        padding-right: 10px !important;
        margin-left: 0 !important; /* Override library default */
      }

      /* Make flag larger */
      intl-tel-input .iti__flag {
        transform: scale(1.2) !important;
        margin-right: 2px !important;
      }

      /* Add horizontal padding to country primary element */
      intl-tel-input .iti__selected-country-primary {
        padding-left: 12px !important;
        padding-right: 12px !important;
      }

      /* Ensure arrow is always visible but smaller */
      intl-tel-input .iti__arrow {
        display: block !important;
        border: none !important;
        width: 0 !important;
        height: 0 !important;
        margin-left: 3px !important;
        border-left: 3px solid transparent !important;
        border-right: 3px solid transparent !important;
        border-top: 3px solid #374151 !important;
      }

      /* Arrow color in disabled state */
      intl-tel-input[disabled] .iti__arrow {
        border-top-color: #9DACC4 !important;
      }

      /* Ensure the selected flag is clickable in enabled state */
      intl-tel-input:not([disabled]) .iti__selected-flag {
        cursor: pointer !important;
      }

      /* Style the search wrapper as a regular row using flexbox */
      intl-tel-input .iti__search-wrapper {
        padding: 8px !important;
        margin: 0 !important;
        display: flex !important;
        align-items: center !important;
        border-bottom: 1px solid #e5e7eb !important;
      }

      /* Additional wrapper if it exists */
      intl-tel-input .iti__search-input-wrapper {
        padding: 8px !important;
      }

      /* Style the search icon - using flexbox layout */
      intl-tel-input .iti__search-icon {
        position: static !important;
        width: 24px !important;
        height: 24px !important;
        flex-shrink: 0 !important;
        margin-right: 12px !important;
        opacity: 1 !important;
        color: #6B778C !important;
        fill: #6B778C !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }

      /* Make the SVG inside the icon full size */
      intl-tel-input .iti__search-icon svg {
        width: 20px !important;
        height: 20px !important;
      }

      /* Style the dropdown search input */
      intl-tel-input .iti__search-input {
        padding: 0 !important;
        height: 24px !important;
        font-size: 14px !important;
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        background-color: transparent !important;
        -webkit-appearance: none !important;
        flex: 1 !important;
        margin: 0 !important;
        line-height: 1.5 !important;
      }

      /* Remove focus outline and border */
      intl-tel-input .iti__search-input:focus {
        outline: none !important;
        border: none !important;
        box-shadow: none !important;
        background-color: transparent !important;
      }

      /* Hide the X clear button in search input */
      intl-tel-input .iti__search-input::-webkit-search-cancel-button,
      intl-tel-input .iti__search-input::-webkit-search-decoration,
      intl-tel-input .iti__search-input::-webkit-search-results-button,
      intl-tel-input .iti__search-input::-webkit-search-results-decoration {
        display: none !important;
        -webkit-appearance: none !important;
      }

      /* Search input placeholder text in gray */
      intl-tel-input .iti__search-input::placeholder {
        color: #9DACC4 !important;
        opacity: 1 !important;
      }

      /* Style country list items with better spacing */
      intl-tel-input .iti__country {
        padding: 10px 16px !important;
        line-height: 1.5 !important;
      }

      /* Country name and dial code styling */
      intl-tel-input .iti__country-name {
        font-size: 14px !important;
        margin-left: 12px !important;
      }

      intl-tel-input .iti__dial-code {
        color: #9DACC4 !important;
        font-size: 14px !important;
      }

      /* Add hover effect for country items */
      intl-tel-input .iti__country:hover {
        background-color: #f3f4f6 !important;
      }

      /* Style the dropdown container */
      intl-tel-input .iti__dropdown-content {
        border-radius: 0 0 8px 8px !important; /* Only round bottom corners */
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
        border: 1px solid #e5e7eb !important;
        margin-top: 0 !important;
      }

      /* Remove bottom border radius only when dropdown is showing (iti--container class is added) */
      intl-tel-input .iti.iti--container {
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      }

      /* Also remove radius from the input inside when dropdown is open */
      intl-tel-input .iti.iti--container input[type="tel"] {
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      }

      /* Ensure it applies to all variants of the .iti container */
      intl-tel-input .iti.iti--allow-dropdown.iti--show-flags.iti--inline-dropdown.iti--container {
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      }

      /* Ensure flag container has consistent size with search icon */
      intl-tel-input .iti__flag-box {
        width: 24px !important;
        height: 24px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin-right: 12px !important;
      }

      /* Make flags in dropdown slightly larger */
      intl-tel-input .iti__flag-box .iti__flag {
        transform: scale(1.1) !important;
      }
    `;
    document.head.appendChild(style);
    this.styleInjected = true;
  }

  connectedCallback() {
    // Add layout styles
    this.addLayoutStyles();

    // Add disabled state styles
    this.addDisabledStyles();

    // Create the input element
    this.input = document.createElement("input");
    this.input.type = "tel";

    // Check if disabled
    const isDisabled = this.hasAttribute("disabled");

    // Default Tailwind classes for consistent styling
    const defaultClasses = [
      "tw-w-full",
      "tw-h-10",
      "tw-px-3",
      "tw-border",
      "tw-border-gray-300",
      "tw-rounded-md",
      "tw-focus:outline-none",
      "tw-focus:ring-2",
      "tw-focus:ring-blue-500"
    ];

    // Add disabled-specific classes
    if (isDisabled) {
      defaultClasses.push("tw-bg-gray-49");
      this.input.disabled = true;
    }

    // Get custom classes if provided
    const customClasses = this.getAttribute("input-class");

    // Apply default classes using classList for security
    defaultClasses.forEach(cls => {
      if (cls.trim()) this.input.classList.add(cls.trim());
    });

    // If custom classes are provided, sanitize and add them
    if (customClasses) {
      customClasses.split(' ').forEach(cls => {
        if (cls.trim()) this.input.classList.add(cls.trim());
      });
    }

    // Set any initial value
    const initialValue = this.getAttribute("value");
    if (initialValue) {
      this.input.value = initialValue;
    }

    // Set id if provided
    const inputId = this.getAttribute("input-id");
    if (inputId) {
      this.input.id = inputId;
    }

    // Set name if provided
    const inputName = this.getAttribute("name");
    if (inputName) {
      this.input.name = inputName;
    }

    // Set placeholder if provided (before intl-tel-input initializes)
    const customPlaceholder = this.getAttribute("placeholder");
    if (customPlaceholder) {
      this.input.placeholder = customPlaceholder;
    }

    // Add the input to this element
    this.appendChild(this.input);

    // Initialize intl-tel-input
    this.initializeIntlTelInput();
  }

  initializeIntlTelInput() {
    this.utilsReady = false;
    const initialCountry = this.getAttribute("initial-country") || "auto";
    const preferredCountries = this.getAttribute("preferred-countries");
    const onlyCountries = this.getAttribute("only-countries");
    const excludeCountries = this.getAttribute("exclude-countries");

    const locale = getUserLocale();
    const translations = getTranslations(locale);
    const initialValue = this.getAttribute("value") || "";
    const hasInternationalNumber = initialValue.startsWith("+");

 
    this.userPreferredCountry = hasInternationalNumber ? null
      : (initialCountry === "auto" ? getCountryCodeFromLocale(locale) : initialCountry);

    const customPlaceholder = this.getAttribute('placeholder');

    const options = {
      initialCountry: hasInternationalNumber ? "" : initialCountry,
      separateDialCode: true,
      nationalMode: false,
      autoPlaceholder: customPlaceholder ? "off" : "aggressive",
      formatOnDisplay: true,
      allowDropdown: true,
      i18n: translations,
      validationNumberTypes: ["MOBILE", "FIXED_LINE"],
      geoIpLookup: (callback) => {
        if (hasInternationalNumber) return; 
        callback(initialCountry === "auto" ? getCountryCodeFromLocale(locale) : initialCountry);
      },
      loadUtils: () => import("intl-tel-input/build/js/utils.js"),
    };

    // Add optional country filters
    if (preferredCountries) {
      options.preferredCountries = preferredCountries.split(",");
    }
    if (onlyCountries) {
      options.onlyCountries = onlyCountries.split(",");
    }
    if (excludeCountries) {
      options.excludeCountries = excludeCountries.split(",");
    }

    // Initialize the plugin
    this.iti = intlTelInput(this.input, options);

    // Apply styles and update placeholder after DOM is ready
    requestAnimationFrame(() => {
      if (!this.iti || !this.input) return;
      this.applyStyles();
      this.updatePlaceholder();
      // Capture detected country from number to preserve on re-init
      if (hasInternationalNumber) {
        this.userPreferredCountry = this.iti.getSelectedCountryData()?.iso2;
      }
    });

    this.setupEventListeners();

    // Sync Elm state once utils are loaded: handleChange() is blocked until then (utils
    // are required for accurate isValidNumber()), so any premature countrychange events
    // or user keystrokes during the loading window aren't reflected in Elm yet. Firing
    // unconditionally covers both prefilled inputs and typing-during-load.
    // Capture the current iti so a pending promise from a prior init (e.g. disabled
    // attribute toggle re-runs initializeIntlTelInput) doesn't double-fire handleChange
    // against the new instance, and skip entirely if the element has been disconnected.
    const capturedIti = this.iti;
    const flushOnReady = () => {
      if (!this.isConnected || this.iti !== capturedIti || !this.input) return;
      this.utilsReady = true;
      this.handleChange();
    };
    this.iti.promise.then(flushOnReady).catch((error) => {
      // Utils chunk failed to load (network/CSP). Unblock handleChange so the input
      // still emits events — isValidNumber() will return false without utils, but at
      // least Elm stays in sync instead of going permanently silent.
      console.error("intl-tel-input utils failed to load", error);
      flushOnReady();
    });
  }

  applyStyles() {
    // Set data attribute for CSS targeting
    this.setAttribute("data-intl-tel-input-init", "true");

    const itiContainer = this.querySelector(".iti");
    if (itiContainer) {
      itiContainer.style.width = "100%";
      itiContainer.style.display = "flex";
      itiContainer.style.flexDirection = "row";
      itiContainer.style.alignItems = "stretch";
    }

    const flagContainer = this.querySelector(".iti__flag-container");
    if (flagContainer) {
      flagContainer.style.height = "100%";
      flagContainer.style.display = "flex";
      flagContainer.style.alignItems = "center";
      // Apply the vertical border directly via JavaScript for better control
      flagContainer.style.borderRight = "1px solid #d1d5db";
      flagContainer.style.paddingRight = "10px";
      flagContainer.style.marginRight = "10px";
    }

    const selectedFlag = this.querySelector(".iti__selected-flag");
    if (selectedFlag) {
      selectedFlag.style.height = "100%";
      selectedFlag.style.display = "flex";
      selectedFlag.style.alignItems = "center";
      selectedFlag.style.justifyContent = "center";
    }

    const dialCode = this.querySelector(".iti__selected-dial-code");
    if (dialCode) {
      dialCode.style.display = "inline-flex";
      dialCode.style.alignItems = "center";
      dialCode.style.height = "100%";
      dialCode.style.lineHeight = "1";
    }

    // Input styling is handled by Tailwind classes, only set flex properties
    if (this.input) {
      this.input.style.minWidth = "0";
      this.input.style.flex = "1 1 0%";
    }
  }

  updatePlaceholder() {
    if (this.input) {
      // Use custom placeholder attribute if provided, otherwise use country-specific example
      const customPlaceholder = this.getAttribute('placeholder');
      if (customPlaceholder) {
        this.input.placeholder = customPlaceholder;
      } else if (this.iti) {
        const countryPlaceholder = this.iti.getSelectedCountryData().placeholder;
        if (countryPlaceholder) {
          this.input.placeholder = countryPlaceholder;
        }
      }
    }
  }

  setupEventListeners() {
    // Detect user-initiated country selection from dropdown
    this.globalClickHandler = (e) => {
      const countryItem = e.target.closest('.iti__country');
      if (countryItem) {
        const dropdown = countryItem.closest('.iti__dropdown-content');
        if (dropdown && this.contains(dropdown.parentElement)) {
          this.allowCountryChange = true;
        }
      }
    };
    document.addEventListener('click', this.globalClickHandler, true);

    // Handle country changes
    this.countryChangeHandler = () => {
      const countryData = this.iti.getSelectedCountryData();

      if (this.allowCountryChange) {
        // User manually selected from dropdown - update preferred country
        this.userPreferredCountry = countryData?.iso2;
        this.allowCountryChange = false;

        // Clear phone number input when user manually selects a new country
        // This affects all phone inputs including login page and MFA management
        this.input.value = "";
        // Trigger change event to notify Elm of the cleared value
        this.handleChange();
        this.updatePlaceholder();
        this.applyStyles();
        return;
      } else if (this.userPreferredCountry && countryData?.iso2 !== this.userPreferredCountry) {
        // Library auto-detection tried to change country - revert to preferred
        this.iti.setCountry(this.userPreferredCountry);
        return;
      }

      this.handleChange();
      this.updatePlaceholder();
      this.applyStyles();
    };
    this.input.addEventListener("countrychange", this.countryChangeHandler);

    // Handle input changes
    this.inputChangeHandler = () => {
      this.handleChange();
    };
    this.input.addEventListener("input", this.inputChangeHandler);

    // Clear dropdown search input and reset country list when dropdown opens
    this.dropdownOpenHandler = () => {
      const searchInput = this.querySelector('.iti__search-input');
      if (searchInput && searchInput.value !== '') {
        searchInput.value = '';
        // Dispatch input event to trigger the library's search filter reset
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };
    this.input.addEventListener("open:countrydropdown", this.dropdownOpenHandler);
  }

  handleChange() {
    if (!this.iti || !this.input) return;
    if (!this.utilsReady) return;

    try {
      const countryData = this.iti.getSelectedCountryData() || {};
      const phoneNumber = this.iti.getNumber() || "";
      const isValid = this.iti.isValidNumber() || false;

      // Dispatch a custom event with all the data
      const event = new CustomEvent("phonechange", {
        detail: {
          phoneNumber: phoneNumber,
          countryCode: countryData.dialCode || "",
          countryIso2: countryData.iso2 || "",
          isValid: isValid,
          nationalNumber: this.input.value || "",
        },
        bubbles: true,
        composed: true,
      });

      this.dispatchEvent(event);

      // Also dispatch for Elm ports if they exist
      if (window.AVT && window.AVT.app && window.AVT.app.ports && window.AVT.app.ports.onIntlTelInputChange) {
        window.AVT.app.ports.onIntlTelInputChange.send({
          inputId: this.input.id || "",
          phoneNumber: phoneNumber,
          countryCode: countryData.dialCode || "",
          nationalNumber: this.input.value || "",
          isValid: isValid,
        });
      }
    } catch (e) {
      // Library not fully initialized yet, skip this call
      return;
    }
  }

  // Getter/setter for value
  get value() {
    return this.iti ? this.iti.getNumber() : this.input ? this.input.value : "";
  }

  set value(val) {
    if (this.iti && this.input) {
      this.iti.setNumber(val);
    } else if (this.input) {
      this.input.value = val;
    }
  }

  // Method to get validation status
  isValid() {
    return this.iti ? this.iti.isValidNumber() : false;
  }

  // Method to set country
  setCountry(countryCode) {
    if (this.iti) {
      this.iti.setCountry(countryCode);
      this.updatePlaceholder();
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    document.removeEventListener('click', this.globalClickHandler, true);
    this.input?.removeEventListener('countrychange', this.countryChangeHandler);
    this.input?.removeEventListener('input', this.inputChangeHandler);
    this.input?.removeEventListener('open:countrydropdown', this.dropdownOpenHandler);

    // Clean up intl-tel-input
    if (this.iti) {
      this.iti.destroy();
    }
  }
}

// Define the custom element
customElements.define("intl-tel-input", IntlTelInputElement);

export default {
  init: () => {
    // Component is self-initializing via custom elements
    console.log("intl-tel-input custom element registered");
  }
};
